import type {
  AdminChatReport,
  AdminPost,
  AdminPostListResponse,
  AdminSuggestion,
  AdminUser,
  AdminUserListResponse,
} from '@tomato/shared';
import { BOARD_LABELS, reportCategoryLabel } from '@tomato/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../api/client';
import { Button, EmptyState, Notice, Spinner, TextField } from '../components/ui';
import { formatWhen } from '../lib/markdown';
import { useSessionStore } from '../store/session';

interface Stats {
  users: number;
  activeUsers: number;
  posts: number;
  postsThisWeek: number;
  writersThisWeek: number;
  comments: number;
  marketTotal: number;
  marketSold: number;
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-2xl bg-surface p-4 shadow-card">
      <p className="text-[12px] text-ink-400">{label}</p>
      <p className="mt-1 text-[22px] font-semibold tracking-tight tabular-nums text-ink-900">
        {value}
      </p>
      {sub && <p className="mt-0.5 text-[12px] text-ink-400">{sub}</p>}
    </div>
  );
}

function StatsPanel() {
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    void api
      .get<{ stats: Stats }>('/api/admin/stats')
      .then((res) => setStats(res.stats))
      .catch(() => {
        /* 지표는 없어도 화면이 동작해야 한다 */
      });
  }, []);

  if (!stats) return null;

  const conversion =
    stats.marketTotal > 0 ? Math.round((stats.marketSold / stats.marketTotal) * 100) : 0;

  return (
    <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatCard label="유저" value={`${stats.users}`} sub={`활성 ${stats.activeUsers}`} />
      <StatCard label="글" value={`${stats.posts}`} sub={`이번 주 ${stats.postsThisWeek}`} />
      {/* H0 킬 크리테리아: 4주차 주당 3건 미만이면 포맷 재검토 */}
      <StatCard
        label="주간 작성자"
        value={`${stats.writersThisWeek}`}
        sub={`댓글 ${stats.comments}`}
      />
      {/* H1: 중고거래 수요가 실재하는가 */}
      <StatCard
        label="거래 완료율"
        value={`${conversion}%`}
        sub={`${stats.marketSold}/${stats.marketTotal}`}
      />
    </div>
  );
}

function UsersPanel() {
  const myId = useSessionStore((s) => s.me?.id);
  const [items, setItems] = useState<AdminUser[]>([]);
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    const params = query ? `?q=${encodeURIComponent(query)}` : '';
    const res = await api.get<AdminUserListResponse>(`/api/admin/users${params}`);
    setItems(res.items);
  }, [query]);

  useEffect(() => {
    setLoading(true);
    void load()
      .catch(() => setError('불러오지 못했어요.'))
      .finally(() => setLoading(false));
  }, [load]);

  async function toggle(user: AdminUser) {
    setBusy(user.id);
    setError('');
    try {
      const res = await api.patch<{ user: AdminUser }>(`/api/admin/users/${user.id}/active`, {
        isActive: !user.isActive,
      });
      setItems((prev) => prev.map((u) => (u.id === res.user.id ? res.user : u)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '바꾸지 못했어요.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <TextField
        value={q}
        placeholder="활동명 또는 이메일로 찾기"
        className="mb-1 py-2.5 text-[14px]"
        onChange={(e) => setQ(e.target.value)}
      />
      <p className="mb-3 text-[12px] text-ink-400">
        이메일로 찾을 수는 있지만 화면에 표시하지 않아요.
      </p>

      {loading ? (
        <div className="py-16 text-center">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon="🔍" title="해당하는 계정이 없어요" />
      ) : (
        <div className="rounded-2xl bg-surface px-4 shadow-card">
          {items.map((user, i) => (
            <div key={user.id}>
              {i > 0 && <div className="h-px bg-ink-100" />}
              <div className="flex items-center gap-3 py-3.5">
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 text-[14px] font-medium text-ink-900">
                    {user.nickname ?? <span className="text-ink-300">가입 미완</span>}
                    {user.role === 'admin' && (
                      <span className="rounded-md bg-ink-900 px-1.5 py-0.5 text-[10px] text-ink-50">
                        관리자
                      </span>
                    )}
                    {!user.isActive && (
                      <span className="rounded-md bg-tomato-50 px-1.5 py-0.5 text-[10px] text-tomato-700">
                        차단됨
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-[12px] text-ink-400">
                    글 {user.postCount} · 댓글 {user.commentCount} · 가입{' '}
                    {formatWhen(user.createdAt)}
                  </p>
                </div>
                {/* 자기 계정은 잠글 수 없다(서버도 거부). 누를 수 있게 두면 혼란만 준다. */}
                {user.id === myId ? (
                  <span className="px-3 text-[12px] text-ink-300">나</span>
                ) : (
                  <Button
                    variant={user.isActive ? 'danger' : 'secondary'}
                    size="sm"
                    disabled={busy === user.id}
                    onClick={() => void toggle(user)}
                  >
                    {user.isActive ? '차단' : '차단 해제'}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <Notice>{error}</Notice>
    </>
  );
}

function PostsPanel() {
  const [items, setItems] = useState<AdminPost[]>([]);
  const [onlyDeleted, setOnlyDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await api.get<AdminPostListResponse>(
      `/api/admin/posts${onlyDeleted ? '?deleted=1' : ''}`,
    );
    setItems(res.items);
  }, [onlyDeleted]);

  useEffect(() => {
    setLoading(true);
    void load()
      .catch(() => setError('불러오지 못했어요.'))
      .finally(() => setLoading(false));
  }, [load]);

  async function act(post: AdminPost) {
    setBusy(post.id);
    setError('');
    try {
      if (post.deletedAt) await api.post(`/api/admin/posts/${post.id}/restore`);
      else await api.del(`/api/posts/${post.id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '처리하지 못했어요.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="mb-3 flex gap-1">
        <button
          onClick={() => setOnlyDeleted(false)}
          className={`rounded-full px-3 py-1.5 text-[13px] font-medium transition ${
            !onlyDeleted ? 'bg-ink-900 text-ink-50' : 'text-ink-500 hover:bg-ink-100'
          }`}
        >
          전체
        </button>
        <button
          onClick={() => setOnlyDeleted(true)}
          className={`rounded-full px-3 py-1.5 text-[13px] font-medium transition ${
            onlyDeleted ? 'bg-ink-900 text-ink-50' : 'text-ink-500 hover:bg-ink-100'
          }`}
        >
          삭제됨
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon="🗂" title="글이 없어요" />
      ) : (
        <div className="rounded-2xl bg-surface px-4 shadow-card">
          {items.map((post, i) => (
            <div key={post.id}>
              {i > 0 && <div className="h-px bg-ink-100" />}
              <div className="flex items-center gap-3 py-3.5">
                <div className="min-w-0 flex-1">
                  <p className="flex items-baseline gap-1.5">
                    <span className="shrink-0 text-[11px] text-ink-400">
                      {BOARD_LABELS[post.boardSlug]}
                    </span>
                    <Link
                      to={`/posts/${post.id}`}
                      className={`min-w-0 flex-1 truncate text-[14px] font-medium transition hover:text-tomato-600 ${
                        post.deletedAt ? 'text-ink-300 line-through' : 'text-ink-900'
                      }`}
                    >
                      {post.title}
                    </Link>
                  </p>
                  <p className="mt-0.5 text-[12px] text-ink-400">
                    {post.authorNickname ?? '알 수 없음'} · {formatWhen(post.createdAt)} · 댓글{' '}
                    {post.commentCount}
                  </p>
                </div>
                <Button
                  variant={post.deletedAt ? 'secondary' : 'danger'}
                  size="sm"
                  disabled={busy === post.id}
                  onClick={() => void act(post)}
                >
                  {post.deletedAt ? '복구' : '삭제'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Notice>{error}</Notice>
    </>
  );
}

function ReportsPanel() {
  const [items, setItems] = useState<AdminChatReport[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await api.get<{ items: AdminChatReport[] }>('/api/admin/reports');
    setItems(res.items);
  }, []);

  useEffect(() => {
    void load().catch(() => {
      setError('신고를 불러오지 못했어요.');
      setItems([]);
    });
  }, [load]);

  async function lock(roomId: string) {
    setBusy(roomId);
    setError('');
    try {
      await api.post(`/api/admin/chats/${roomId}/lock`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '처리하지 못했어요.');
    } finally {
      setBusy(null);
    }
  }

  if (items === null) {
    return (
      <div className="py-16 text-center">
        <Spinner />
      </div>
    );
  }
  if (items.length === 0) {
    return <EmptyState icon="🚩" title="신고된 대화가 없어요" />;
  }

  return (
    <>
      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.roomId} className="rounded-2xl bg-surface p-4 shadow-card">
            <div className="flex items-center gap-2">
              <span
                className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${
                  r.category === 'harassment'
                    ? 'bg-tomato-600 text-white'
                    : 'bg-ink-100 text-ink-600'
                }`}
              >
                {reportCategoryLabel(r.category)}
              </span>
              <Link
                to={`/posts/${r.postId}`}
                className="min-w-0 flex-1 truncate text-[14px] font-medium text-ink-900 hover:text-tomato-600"
              >
                {r.postTitle}
              </Link>
              {r.closed && (
                <span className="shrink-0 rounded-md bg-ink-100 px-1.5 py-0.5 text-[10px] text-ink-500">
                  잠김
                </span>
              )}
            </div>
            <p className="mt-2 text-[13px] text-ink-700">{r.reportedReason}</p>
            <p className="mt-1 text-[11px] text-ink-400">{formatWhen(r.createdAt)}</p>

            <div className="mt-3 flex items-center gap-1.5">
              {r.snapshot && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpen((v) => (v === r.roomId ? null : r.roomId))}
                >
                  {open === r.roomId ? '캡처 접기' : '대화 캡처 보기'}
                </Button>
              )}
              {!r.closed && (
                <Button
                  variant="danger"
                  size="sm"
                  disabled={busy === r.roomId}
                  onClick={() => void lock(r.roomId)}
                >
                  대화 잠금
                </Button>
              )}
            </div>

            {open === r.roomId && r.snapshot && (
              <div className="mt-3 max-h-96 overflow-y-auto rounded-xl border border-ink-100 bg-ink-50 p-2">
                <img src={r.snapshot} alt="대화 캡처" className="w-full rounded-lg" />
              </div>
            )}
          </div>
        ))}
      </div>
      <Notice>{error}</Notice>
    </>
  );
}

function SuggestionsPanel() {
  const [items, setItems] = useState<AdminSuggestion[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await api.get<{ items: AdminSuggestion[] }>('/api/admin/suggestions');
    setItems(res.items);
  }, []);

  useEffect(() => {
    void load().catch(() => {
      setError('제안을 불러오지 못했어요.');
      setItems([]);
    });
  }, [load]);

  async function toggle(s: AdminSuggestion) {
    setBusy(s.id);
    setError('');
    try {
      await api.patch(`/api/admin/suggestions/${s.id}/resolve`, { resolved: !s.resolved });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '처리하지 못했어요.');
    } finally {
      setBusy(null);
    }
  }

  if (items === null) {
    return (
      <div className="py-16 text-center">
        <Spinner />
      </div>
    );
  }
  if (items.length === 0) {
    return <EmptyState icon="💬" title="아직 들어온 제안이 없어요" />;
  }

  return (
    <>
      <div className="space-y-3">
        {items.map((s) => (
          <div
            key={s.id}
            className={`rounded-2xl bg-surface p-4 shadow-card ${s.resolved ? 'opacity-60' : ''}`}
          >
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-medium text-ink-700">
                {s.authorNickname ?? '(온보딩 미완)'}
              </span>
              <span className="text-[11px] text-ink-400">{formatWhen(s.createdAt)}</span>
              {s.resolved && (
                <span className="ml-auto shrink-0 rounded-md bg-ink-100 px-1.5 py-0.5 text-[10px] text-ink-500">
                  확인함
                </span>
              )}
            </div>
            <p className="mt-2 whitespace-pre-wrap text-[14px] leading-relaxed text-ink-900">
              {s.body}
            </p>
            <div className="mt-3">
              <Button
                variant={s.resolved ? 'ghost' : 'secondary'}
                size="sm"
                disabled={busy === s.id}
                onClick={() => void toggle(s)}
              >
                {s.resolved ? '미확인으로' : '확인함으로'}
              </Button>
            </div>
          </div>
        ))}
      </div>
      <Notice>{error}</Notice>
    </>
  );
}

export function AdminScreen() {
  const [tab, setTab] = useState<'reports' | 'suggestions' | 'posts' | 'users'>('reports');

  return (
    <div className="py-6">
      <h1 className="mb-5 text-[26px] font-semibold tracking-tight text-ink-900">관리</h1>

      <StatsPanel />

      <div className="mb-4 flex gap-6 hairline-b">
        {(['reports', 'suggestions', 'posts', 'users'] as const).map((code) => (
          <button
            key={code}
            onClick={() => setTab(code)}
            className={`relative pb-2.5 text-[14px] font-medium transition ${
              tab === code ? 'text-ink-900' : 'text-ink-400 hover:text-ink-700'
            }`}
          >
            {code === 'reports'
              ? '신고'
              : code === 'suggestions'
                ? '제안'
                : code === 'posts'
                  ? '글'
                  : '계정'}
            {tab === code && (
              <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-tomato-600" />
            )}
          </button>
        ))}
      </div>

      {tab === 'reports' ? (
        <ReportsPanel />
      ) : tab === 'suggestions' ? (
        <SuggestionsPanel />
      ) : tab === 'posts' ? (
        <PostsPanel />
      ) : (
        <UsersPanel />
      )}
    </div>
  );
}
